/**
 * Case/diacritics-insensitive highlight + excerpt helpers for search results.
 * Rendered with `<mark>` segments — never innerHTML.
 */

const DIACRITICS = /[\u0300-\u036f]/g;

/**
 * Lowercase + diacritics stripped, WITHOUT trimming — index-preserving, so positions found
 * on the normalized copy still address the original string.
 */
export function normalizeText(value: string): string {
  return (value ?? '').normalize('NFD').replace(DIACRITICS, '').toLowerCase();
}

/** Normalized + trimmed — what a user-typed term is compared as. */
export function normalizeSearchText(value: string): string {
  return normalizeText(value).trim();
}

export interface HighlightSegment {
  text: string;
  match: boolean;
}

/**
 * Splits `text` on every case/diacritics-insensitive occurrence of `term`.
 * Returns a single non-matching segment when there is nothing to highlight.
 */
export function highlightSegments(text: string, term: string): HighlightSegment[] {
  const source = text ?? '';
  const needle = normalizeSearchText(term);
  if (!source || !needle) return [{ text: source, match: false }];

  const haystack = normalizeText(source);
  const segments: HighlightSegment[] = [];
  let cursor = 0;

  while (cursor < source.length) {
    const found = haystack.indexOf(needle, cursor);
    if (found < 0) {
      segments.push({ text: source.slice(cursor), match: false });
      break;
    }
    if (found > cursor) segments.push({ text: source.slice(cursor, found), match: false });
    segments.push({ text: source.slice(found, found + needle.length), match: true });
    cursor = found + needle.length;
  }

  return segments.filter((segment) => segment.text.length > 0);
}

/** Excerpt centered on the first occurrence of `term`, with ellipses when trimmed. */
export function buildExcerpt(text: string, term: string, radius = 70): string {
  const source = (text ?? '').replace(/\s+/g, ' ').trim();
  const needle = normalizeSearchText(term);
  const maxLength = radius * 2;
  if (!source) return '';

  const index = needle ? normalizeText(source).indexOf(needle) : -1;
  if (index < 0) {
    return source.length > maxLength ? `${source.slice(0, maxLength)}…` : source;
  }

  const start = Math.max(0, index - radius);
  const end = Math.min(source.length, index + needle.length + radius);

  return `${start > 0 ? '…' : ''}${source.slice(start, end)}${end < source.length ? '…' : ''}`;
}
